import { z } from 'zod'

// ============================================================================
// Input
// ============================================================================

export const profitabilityInputSchema = z.object({
  startDate: z.string().date(), // YYYY-MM-DD (avoids timezone issues)
  endDate: z.string().date(),
  employeeIds: z.array(z.number()).optional(),
  maintenanceTaskIds: z.array(z.number()).optional(),
  customerIds: z.array(z.number()).optional(),
  customerTypes: z.array(z.string()).optional(),
})

export type ProfitabilityInput = z.infer<typeof profitabilityInputSchema>

// ============================================================================
// Summary (P&L overview)
// ============================================================================

export const profitabilitySummarySchema = z.object({
  taskRevenue: z.number(),
  lineItemRevenue: z.number(),
  subscriptionRevenue: z.number(),
  totalRevenue: z.number(),
  // Direct costs (allocated to visits)
  laborCost: z.number(),
  materialCost: z.number(),
  commissionCost: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  grossProfit: z.number(),
  grossMargin: z.number(), // 0-1
  // Full costs (real salary costs regardless of utilization)
  fullLaborCost: z.number(),
  fullTotalCost: z.number(),
  fullGrossProfit: z.number(),
  fullGrossMargin: z.number(), // 0-1
  visitCount: z.number(),
  revenuePerVisit: z.number(),
  costPerVisit: z.number(),
  profitPerVisit: z.number(),
})

export type ProfitabilitySummary = z.infer<typeof profitabilitySummarySchema>

// ============================================================================
// Monthly Trend
// ============================================================================

export const profitabilityMonthDataSchema = z.object({
  month: z.string(), // 'YYYY-MM'
  revenue: z.number(),
  cost: z.number(),
  profit: z.number(),
  margin: z.number(),
  fullCost: z.number(),
  fullProfit: z.number(),
  fullMargin: z.number(),
})

export type ProfitabilityMonthData = z.infer<
  typeof profitabilityMonthDataSchema
>

// ============================================================================
// Visit Profitability
// ============================================================================

export const visitProfitabilityRowSchema = z.object({
  visitId: z.number(),
  date: z.string(),
  customerName: z.string().nullable(),
  propertyAddress: z.string().nullable(),
  attendeeNames: z.array(z.string()),
  durationHours: z.number(),
  revenue: z.number(),
  laborCost: z.number(),
  materialCost: z.number(),
  commissionCost: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  profit: z.number(),
  margin: z.number(),
})

export type VisitProfitabilityRow = z.infer<typeof visitProfitabilityRowSchema>

// ============================================================================
// Employee Profitability
// ============================================================================

export const employeeProfitabilityRowSchema = z.object({
  userId: z.number(),
  employeeId: z.number(),
  name: z.string(),
  role: z.string(),
  hoursWorked: z.number(),
  availableHours: z.number(),
  utilizationRate: z.number(), // 0-1
  visitCount: z.number(),
  revenue: z.number(),
  laborCost: z.number(),
  fullCost: z.number(),
  commissionPaid: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  profit: z.number(),
  margin: z.number(),
  fullProfit: z.number(),
  fullMargin: z.number(),
  revenuePerHour: z.number(),
  costPerHour: z.number(),
})

export type EmployeeProfitabilityRow = z.infer<
  typeof employeeProfitabilityRowSchema
>

// ============================================================================
// Customer Profitability
// ============================================================================

export const customerProfitabilityRowSchema = z.object({
  customerId: z.number(),
  customerName: z.string(),
  customerType: z.string(),
  visitCount: z.number(),
  taskRevenue: z.number(),
  lineItemRevenue: z.number(),
  subscriptionRevenue: z.number(),
  totalRevenue: z.number(),
  laborCost: z.number(),
  materialCost: z.number(),
  commissionCost: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  profit: z.number(),
  margin: z.number(),
})

export type CustomerProfitabilityRow = z.infer<
  typeof customerProfitabilityRowSchema
>

// ============================================================================
// Service Profitability
// ============================================================================

export const serviceProfitabilityRowSchema = z.object({
  maintenanceTaskId: z.number(),
  taskName: z.string(),
  category: z.string().nullable(),
  count: z.number(),
  totalRevenue: z.number(),
  avgRevenue: z.number(),
  totalLaborCost: z.number(),
  avgLaborCost: z.number(),
  profit: z.number(),
  margin: z.number(),
})

export type ServiceProfitabilityRow = z.infer<
  typeof serviceProfitabilityRowSchema
>

// ============================================================================
// Service Monthly Trend
// ============================================================================

export const serviceMonthlyTrendRowSchema = z.object({
  month: z.string(), // 'YYYY-MM'
  maintenanceTaskId: z.number(),
  taskName: z.string(),
  revenue: z.number(),
  laborCost: z.number(),
  profit: z.number(),
})

export type ServiceMonthlyTrendRow = z.infer<
  typeof serviceMonthlyTrendRowSchema
>

// ============================================================================
// Segment Monthly Trend
// ============================================================================

export const segmentMonthlyTrendRowSchema = z.object({
  month: z.string(), // 'YYYY-MM'
  customerType: z.string(),
  revenue: z.number(),
  laborCost: z.number(),
  profit: z.number(),
})

export type SegmentMonthlyTrendRow = z.infer<
  typeof segmentMonthlyTrendRowSchema
>

// ============================================================================
// Response schemas with totals
// ============================================================================

export const profitabilityTotalsSchema = z.object({
  revenue: z.number(),
  laborCost: z.number(),
  materialCost: z.number(),
  commissionCost: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  profit: z.number(),
  margin: z.number(),
})

export type ProfitabilityTotals = z.infer<typeof profitabilityTotalsSchema>
